import { useEffect, useState } from 'react'

// A tab opened before a deploy still points at the old route chunks. TanStack Router answers the failed
// import by reloading the page once, but the old document keeps running until the new one arrives and can
// render the import error again in the meantime. location.reload() fires beforeunload first, which tells
// that moment apart from a chunk still missing after the reload, which must still show the error.
let unload: Event | undefined
window.addEventListener('beforeunload', (event) => {
  unload = event
})
// A back/forward-cache restore brings back a document that saw beforeunload but never unloaded.
window.addEventListener('pageshow', () => {
  unload = undefined
})

// A beforeunload can also pass without unloading the page (a same-tab file download, a tel: or mailto:
// link), and nothing announces that. Past this wait, the reload is taken as not coming and the error shows.
export const RELOAD_WAIT_MS = 10_000

// Chrome, Firefox and Safari's messages for a dynamic import that could not be loaded
const MISSING_CHUNK_MESSAGES = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
]

function isReloadingForMissingChunk(error: unknown): boolean {
  // Another listener that cancels the unload (an unsaved form) keeps this document alive.
  if (!unload || unload.defaultPrevented) return false
  if (!(error instanceof Error)) return false
  return MISSING_CHUNK_MESSAGES.some((prefix) => error.message.startsWith(prefix))
}

// Every route errorComponent needs this check: a route's own boundary catches its stale-chunk error first.
export function useIsReloadingForMissingChunk(error: unknown): boolean {
  const reloading = isReloadingForMissingChunk(error)
  const [waitedTooLong, setWaitedTooLong] = useState(false)

  useEffect(() => {
    if (!reloading) return undefined
    const timer = setTimeout(() => setWaitedTooLong(true), RELOAD_WAIT_MS)
    return () => clearTimeout(timer)
  }, [reloading])

  return reloading && !waitedTooLong
}
